import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { SafeReportResponse } from "../Conversion";
import { useMemo, useState } from "react";
import { useToast } from "../../../context-providers/Toast";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import {
  useCalculatedPremium,
  useGeneratePolicyFlyApplication,
  useUpdatePolicyFlyApplication,
  useLinkPolicyFlyApplication,
  useUnlinkPolicyFlyApplication,
  useLocalUser,
  useThisReportIsUpdating,
} from "../data";
import styles from "./PolicyFlyCard.module.css";
import { perilSentenceFromEnabledPerils } from "../general-input/util";
import {
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "../layers/util";
import * as client from "../../../client";
import { Message } from "primereact/message";
import { formatExactMoneyDollars, LayerPremiumSummary } from "../Money";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";

type PolicyFlyModalProps = {
  specialtyPropertyInfo: SafeReportResponse;
  visible: boolean;
  setVisible: (value: boolean) => void;
};

export const PolicyFlyModal: React.FC<PolicyFlyModalProps> = ({
  specialtyPropertyInfo,
  visible,
  setVisible,
}) => {
  const toast = useToast();
  const companyInfo = specialtyPropertyInfo.report_json.company_info;
  const allPerils =
    specialtyPropertyInfo.report_json.policy_info?.included_perils ?? [];
  const perilSentenceOverride =
    specialtyPropertyInfo.report_json.policy_info?.peril_sentence_override;
  const allLayers = sortedLayersFromReport(specialtyPropertyInfo);

  const premium = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    visible
  );

  // Build per-layer peril sentences for the API call and display
  const perilsByLayer = useMemo(() => {
    const result: Record<string, string> = {};
    for (const layer of allLayers) {
      if (!layer.is_enabled || !layer.layer_id) continue;
      result[layer.layer_id] = perilSentenceFromEnabledPerils(
        allPerils,
        perilSentenceOverride
      );
    }
    return result;
  }, [allLayers, allPerils, perilSentenceOverride]);

  // Build a lookup from layer_id to premium totals for the selected source
  const premiumByLayer = useMemo(() => {
    const result: Record<string, client.LayerTotals> = {};
    if (!premium.data) return result;
    for (const layerPremium of premium.data.layers) {
      const layer = allLayers.find((l) => l.layer_id === layerPremium.layer_id);
      if (!layer) continue;
      const totals = layerPremium.totals["resolved"];
      if (totals) {
        result[layerPremium.layer_id] = totals;
      }
    }
    return result;
  }, [premium, allLayers]);
  const enabledLayers = allLayers.filter((layer) => layer.is_enabled);

  const policyFlyInfo = specialtyPropertyInfo.report_json.policy_fly_info;
  const applications = policyFlyInfo?.applications ?? [];

  // Build a lookup from layer_id to its applications. A layer can have more
  // than one application, so collect them into a list.
  const appsByLayerId = useMemo(() => {
    const map = new Map<string, client.PolicyFlyApplication[]>();
    for (const app of applications) {
      const list = map.get(app.layer_id) ?? [];
      list.push(app);
      map.set(app.layer_id, list);
    }
    return map;
  }, [applications]);

  const completedApplications = applications.filter((app) => !!app.url);
  const layersWithoutApps = enabledLayers.filter((layer) => {
    const apps = appsByLayerId.get(layer.layer_id ?? "") ?? [];
    return !apps.some((app) => !!app.url);
  });
  const allLayersHaveUrls =
    enabledLayers.length > 0 && layersWithoutApps.length === 0;

  // Check if a Policy Fly task is already running via the generalized polling hook
  const isPolicyFlyRunning = useThisReportIsUpdating(
    specialtyPropertyInfo.report_id,
    "POLICY_FLY"
  );

  const generateApplication = useGeneratePolicyFlyApplication(
    specialtyPropertyInfo.report_id
  );

  const updateApplication = useUpdatePolicyFlyApplication(
    specialtyPropertyInfo.report_id
  );

  const linkApplication = useLinkPolicyFlyApplication(
    specialtyPropertyInfo.report_id
  );

  const unlinkApplication = useUnlinkPolicyFlyApplication(
    specialtyPropertyInfo.report_id
  );

  const canManuallyLink = useFeatureEnabled(
    "policy-fly-manual-linking",
    undefined,
    useLocalUser()
  );

  const isTaskActive =
    !!isPolicyFlyRunning ||
    generateApplication.isPending ||
    updateApplication.isPending ||
    linkApplication.isPending ||
    unlinkApplication.isPending;

  // Next report-global unused suffix letter (mirrors the backend generator).
  const nextSuffix = useMemo(() => {
    const used = new Set(applications.map((app) => app.suffix));
    let idx = 0;
    while (used.has(String.fromCharCode("A".charCodeAt(0) + idx))) idx++;
    return String.fromCharCode("A".charCodeAt(0) + idx);
  }, [applications]);

  // Admin-only "link existing application" form state.
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [linkLayerId, setLinkLayerId] = useState("");
  const [linkSuffix, setLinkSuffix] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const openLinkForm = () => {
    setLinkLayerId(enabledLayers[0]?.layer_id ?? "");
    setLinkSuffix(nextSuffix);
    setLinkUrl("");
    setLinkFormOpen(true);
  };

  const trimmedLinkSuffix = linkSuffix.trim();
  const trimmedLinkUrl = linkUrl.trim();
  const suffixInUse = applications.some(
    (app) => app.suffix === trimmedLinkSuffix
  );
  const linkFormValid =
    !!linkLayerId && !!trimmedLinkSuffix && !suffixInUse && !!trimmedLinkUrl;

  const handleLink = async () => {
    try {
      await linkApplication.mutateAsync({
        layerId: linkLayerId,
        suffix: trimmedLinkSuffix,
        url: trimmedLinkUrl,
      });
      setLinkFormOpen(false);
      toast.show({
        severity: "success",
        summary: "Linked Policy Fly Application",
        detail: `Linked application ${trimmedLinkSuffix}.`,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Failed to Link Policy Fly Application",
        detail: "Could not link the existing Policy Fly application.",
        sticky: true,
      });
    }
  };

  const handleUnlink = async (app: client.PolicyFlyApplication) => {
    try {
      await unlinkApplication.mutateAsync({ suffix: app.suffix });
      toast.show({
        severity: "success",
        summary: "Unlinked Policy Fly Application",
        detail: `Unlinked application ${app.suffix}.`,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Failed to Unlink Policy Fly Application",
        detail: "Could not unlink the Policy Fly application.",
        sticky: true,
      });
    }
  };

  const confirmUnlink = (app: client.PolicyFlyApplication, label: string) => {
    confirmDialog({
      header: `Unlink ${label}?`,
      message:
        "This removes the link to this application from the report. The " +
        "application in PolicyFly is not affected.",
      acceptLabel: "Unlink",
      rejectLabel: "Cancel",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => handleUnlink(app),
    });
  };

  const handleCreate = async (additionalLayerId?: string) => {
    try {
      await generateApplication.mutateAsync({
        perilsByLayer,
        additionalLayerId,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Failed to Generate Policy Fly Application",
        detail: "Could not start the Policy Fly application request.",
        sticky: true,
      });
    }
  };

  const layerForApp = (
    app: client.PolicyFlyApplication
  ): client.Layer | undefined => {
    return allLayers.find((l) => l.layer_id === app.layer_id);
  };

  const handleUpdate = async (app: client.PolicyFlyApplication) => {
    try {
      await updateApplication.mutateAsync({
        suffix: app.suffix,
        perilsInsuredDescription: perilsByLayer[app.layer_id] ?? "",
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Failed to Update Policy Fly Application",
        detail: "Could not start the Policy Fly application update.",
        sticky: true,
      });
    }
  };

  const confirmUpdate = (app: client.PolicyFlyApplication, label: string) => {
    confirmDialog({
      header: `Update ${label}?`,
      message:
        "This will pull the application back to Draft in PolicyFly and " +
        "overwrite it with this report's current data.",
      acceptLabel: "Update application",
      rejectLabel: "Cancel",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => handleUpdate(app),
    });
  };

  return (
    <Dialog
      header="Policy Fly Application"
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => setVisible(false)}
      footer={
        <div>
          <Button
            label={`Create ${layersWithoutApps.length} Policy Fly application${layersWithoutApps.length === 1 ? "" : "s"}`}
            loading={isTaskActive}
            onClick={() => handleCreate()}
            disabled={
              allLayersHaveUrls || enabledLayers.length === 0 || !premium.data
            }
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Named insured</span>
          <span>{companyInfo.company_name}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Mailing address</span>
          <span>{`${companyInfo.company_address}, ${companyInfo.company_city}, ${companyInfo.company_state}, ${companyInfo.company_zip}`}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Effective date</span>
          <span>{companyInfo.effective_date}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Expiration date</span>
          <span>{companyInfo.expiration_date}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Total insured value</span>
          <span>
            {formatExactMoneyDollars(
              specialtyPropertyInfo.precomputed_metadata.total_tiv
            )}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Layers</span>
          {enabledLayers.length === 0 && (
            <div>
              <Message severity="warn" text="Need at least 1 enabled layer" />
            </div>
          )}
          <div className={styles.layerList}>
            {enabledLayers.map((layer) => {
              const totals = premiumByLayer[layer.layer_id ?? ""];
              const layerApps = appsByLayerId.get(layer.layer_id ?? "") ?? [];
              const completedCount = layerApps.filter(
                (app) => !!app.url
              ).length;
              return (
                <div key={layer.layer_id} className={styles.layerRow}>
                  <span>{readableLimitOfLiabilityFromLayer(layer)}</span>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--text-color-secondary)",
                    }}
                  >
                    {perilsByLayer[layer.layer_id ?? ""] ?? ""}
                  </div>
                  {totals && (
                    <LayerPremiumSummary
                      layerTotal={totals.layer_total}
                      shareTotal={totals.share_total}
                    />
                  )}
                  {completedCount > 0 && layer.layer_id && (
                    <Button
                      label={`Create another application for this layer (${completedCount} so far)`}
                      icon="pi pi-plus"
                      outlined
                      severity="secondary"
                      size="small"
                      loading={isTaskActive}
                      disabled={isTaskActive || !premium.data}
                      onClick={() => handleCreate(layer.layer_id ?? undefined)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {completedApplications.length > 0 && (
          <div className={styles.verticalContainerSmall}>
            <span className={styles.header}>Policy Fly Applications</span>
            {completedApplications.map((app) => {
              const layer = layerForApp(app);
              const label = layer
                ? `${readableLayer(layer)} - ${app.suffix}`
                : `Layer ${app.suffix}`;
              const appUrl = app.url;
              return (
                <div key={app.suffix} className={styles.appRow}>
                  <SplitButton
                    label={label}
                    icon="pi pi-external-link"
                    outlined
                    onClick={() => {
                      if (appUrl) {
                        window.open(appUrl, "_blank", "noopener,noreferrer");
                      }
                    }}
                    model={[
                      {
                        label: app.request_id
                          ? "Update with current report data"
                          : "Update unavailable",
                        icon: "pi pi-refresh",
                        disabled:
                          isTaskActive || !premium.data || !app.request_id,
                        command: () => confirmUpdate(app, label),
                      },
                      ...(canManuallyLink
                        ? [
                            {
                              label: "Unlink from report",
                              icon: "pi pi-times",
                              disabled: isTaskActive,
                              command: () => confirmUnlink(app, label),
                            },
                          ]
                        : []),
                    ]}
                  />
                  {!app.request_id && (
                    <Message
                      severity="warn"
                      text="Updating isn't available for this application because it has no linked PolicyFly request id."
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {canManuallyLink && (
          <div className={styles.verticalContainerSmall}>
            <span className={styles.header}>Link existing application</span>
            <div
              style={{
                fontSize: "0.85rem",
                color: "var(--text-color-secondary)",
              }}
            >
              Link a PolicyFly application that was created directly in
              PolicyFly. This only records the link on this report; it does not
              create or modify anything in PolicyFly.
            </div>
            {!linkFormOpen ? (
              <div>
                <Button
                  label="Link existing application"
                  icon="pi pi-link"
                  outlined
                  size="small"
                  disabled={isTaskActive || enabledLayers.length === 0}
                  onClick={openLinkForm}
                />
              </div>
            ) : (
              <div className={styles.linkForm}>
                <div className={styles.linkFormField}>
                  <span className={styles.linkFormLabel}>Layer</span>
                  <Dropdown
                    value={linkLayerId}
                    onChange={(e) => setLinkLayerId(e.value)}
                    options={enabledLayers
                      .filter((layer) => !!layer.layer_id)
                      .map((layer) => ({
                        label: readableLayer(layer),
                        value: layer.layer_id,
                      }))}
                    placeholder="Select a layer"
                  />
                </div>
                <div className={styles.linkFormField}>
                  <span className={styles.linkFormLabel}>Suffix</span>
                  <InputText
                    value={linkSuffix}
                    onChange={(e) => setLinkSuffix(e.target.value)}
                    className={suffixInUse ? "p-invalid" : undefined}
                  />
                  {suffixInUse && (
                    <small className="p-error">
                      Suffix {trimmedLinkSuffix} is already in use on this
                      report.
                    </small>
                  )}
                </div>
                <div className={styles.linkFormField}>
                  <span className={styles.linkFormLabel}>
                    PolicyFly application URL
                  </span>
                  <InputText
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className={styles.linkFormActions}>
                  <Button
                    label="Link application"
                    icon="pi pi-link"
                    size="small"
                    loading={linkApplication.isPending}
                    disabled={!linkFormValid || isTaskActive}
                    onClick={handleLink}
                  />
                  <Button
                    label="Cancel"
                    outlined
                    severity="secondary"
                    size="small"
                    disabled={linkApplication.isPending}
                    onClick={() => setLinkFormOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
